import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Sparkles, 
  Users, 
  Calendar,
  Clock, 
  ArrowRight,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  MapPin,
  Activity
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const PlanGenerator = () => {
  // const navigate = useNavigate(); // Reserved for future navigation
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4; // Added step 4 for ranking
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [curatedActivities, setCuratedActivities] = useState(null);
  const [primaryActivities, setPrimaryActivities] = useState([]);
  const [backupActivities, setBackupActivities] = useState([]);
  const [votingSessionId, setVotingSessionId] = useState(null);
  const [activityVotes, setActivityVotes] = useState({}); // { activityId: 'yes' | 'no' }
  const [votingInProgress, setVotingInProgress] = useState({}); // Track which activity is being voted on
  const [currentCardIndex, setCurrentCardIndex] = useState(0); // Track which card is currently shown
  // const [topActivities, setTopActivities] = useState([]); // Top 5 activities for ranking (reserved for future use)
  const [rankingActivities, setRankingActivities] = useState([]); // Activities in ranking order
  const [isProcessingVotes, setIsProcessingVotes] = useState(false);
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);

  // Drag and drop sensors (must be at component level, not inside render function)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Step 1 Data: Group/Friends, Date, Time, Number of Activities
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [planDate, setPlanDate] = useState('');
  const [planTime, setPlanTime] = useState('evening');
  const [numActivities, setNumActivities] = useState(3);
  
  // Step 2 Data: Vibe Selection
  const [selectedVibes, setSelectedVibes] = useState([]);
  
  // Preferences (aggregated)
  const [preferences, setPreferences] = useState({
    groupSize: 2,
    budget: 'medium',
    timeOfDay: 'evening',
    vibe: 'casual',
    location: 'rutgers-new-brunswick',
    dietaryRestrictions: [],
    activities: []
  });

  const vibeOptions = [
    { value: 'casual', label: 'Casual', icon: '😎', description: 'Relaxed and laid-back' },
    { value: 'upscale', label: 'Upscale', icon: '✨', description: 'Elegant and refined' },
    { value: 'adventurous', label: 'Adventurous', icon: '🎯', description: 'Exciting and bold' },
    { value: 'chill', label: 'Chill', icon: '🧘', description: 'Calm and peaceful' },
    { value: 'party', label: 'Party', icon: '🎉', description: 'High energy and fun' },
    { value: 'romantic', label: 'Romantic', icon: '💕', description: 'Intimate and cozy' }
  ];

  const timeOptions = [
    { value: 'afternoon', label: 'Afternoon (12-5 PM)', icon: '☀️' },
    { value: 'evening', label: 'Evening (5-9 PM)', icon: '🌆' },
    { value: 'night', label: 'Night (9 PM+)', icon: '🌙' }
  ];

  const locationOptions = [
    { value: 'rutgers-new-brunswick', label: 'Rutgers New Brunswick', icon: '🎓' },
    { value: 'nyc', label: 'New York City', icon: '🗽' },
    { value: 'philadelphia', label: 'Philadelphia', icon: '🔔' },
    { value: 'bloomfield', label: 'Bloomfield, NJ', icon: '🏛️' },
    { value: 'austin', label: 'Austin, TX', icon: '🎸' },
    { value: 'nashville', label: 'Nashville, TN', icon: '🎵' }
  ];

  // Fetch friends and groups on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user || !userProfile) return;
      
      try {
        setLoading(true);
        const token = await user.getIdToken();
        
        const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:5001/api';
        const cacheBuster = new Date().getTime();
        
        // Fetch user's friends
        const friendsResponse = await fetch(`${apiBase}/friends/${userProfile.user_id}?_t=${cacheBuster}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (friendsResponse.ok) {
          const friendsData = await friendsResponse.json();
          setFriends(friendsData.data || []);
        } else {
          console.error('Failed to fetch friends:', friendsResponse.status);
        }
        
        // Fetch user's groups
        const groupsResponse = await fetch(`${apiBase}/groups/user/${userProfile.user_id}?_t=${cacheBuster}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData.data || []);
        } else {
          console.error('Failed to fetch groups:', groupsResponse.status);
        }
        
        // Check if groupId is provided in URL
        const groupId = searchParams.get('groupId');
        if (groupId) {
          const groupResponse = await fetch(`${apiBase}/groups/${groupId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (groupResponse.ok) {
            const groupData = await groupResponse.json();
            setSelectedGroup(groupData.data);
            const groupMemberIds = groupData.data.members.map(member => member.user_id);
            setSelectedFriends(groupMemberIds);
            setPreferences(prev => ({
              ...prev,
              groupSize: groupData.data.member_count
            }));
          }
        }
        
        // Set default date to today
        const today = new Date();
        const dateString = today.toISOString().split('T')[0];
        setPlanDate(dateString);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, userProfile, searchParams]);

  const handleNext = () => {
    // Validation
    if (currentStep === 1) {
      // Allow proceeding without group/friends (solo plan)
      // Validation only for date and activities
      if (!planDate) {
        toast.error('Please select a date');
        return;
      }
      if (numActivities < 1 || numActivities > 10) {
        toast.error('Number of activities must be between 1 and 10');
        return;
      }
      
      // Update group size based on selection (or default to 1 for solo)
      const groupSize = selectedGroup 
        ? selectedGroup.member_count || selectedGroup.members?.length || 1
        : selectedFriends.length > 0 
          ? selectedFriends.length + 1 // +1 for current user
          : 1; // Solo plan
      
      setPreferences(prev => ({
        ...prev,
        groupSize: groupSize
      }));
    }
    
    if (currentStep === 2) {
      if (selectedVibes.length === 0) {
        toast.error('Please select at least one vibe');
        return;
      }
      
      // Aggregate vibes (use first one as primary, but pass all)
      const groupSize = selectedGroup 
        ? selectedGroup.member_count || selectedGroup.members?.length || 1
        : selectedFriends.length > 0 
          ? selectedFriends.length + 1 // +1 for current user
          : 1; // Solo plan
      
    setPreferences(prev => ({
      ...prev,
        vibe: selectedVibes[0],
        timeOfDay: planTime,
        groupSize: groupSize
      }));
      
      // Move to curation step
      curateActivities();
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setSelectedFriends([]); // Clear friends when group selected
    setPreferences(prev => ({
      ...prev,
      groupSize: group.member_count
    }));
  };

  const handleFriendToggle = (friendId) => {
    let newSelectedFriends;
    if (selectedFriends.includes(friendId)) {
      // Removing friend
      newSelectedFriends = selectedFriends.filter(id => id !== friendId);
      setSelectedFriends(newSelectedFriends);
    } else {
      // Adding friend
      newSelectedFriends = [...selectedFriends, friendId];
      setSelectedFriends(newSelectedFriends);
      setSelectedGroup(null); // Clear group when friends selected
    }
    // Update group size based on new selection
    setPreferences(prev => ({
      ...prev,
      groupSize: newSelectedFriends.length + 1 // +1 for current user
    }));
  };

  const handleVibeToggle = (vibe) => {
    if (selectedVibes.includes(vibe)) {
      setSelectedVibes(selectedVibes.filter(v => v !== vibe));
    } else {
      // Allow multiple vibes
      setSelectedVibes([...selectedVibes, vibe]);
    }
  };

  const curateActivities = async () => {
    setIsGenerating(true);
    toast.loading('Curating personalized activities...');

    try {
      // Calculate group size (default to 1 for solo plan)
      const groupSize = selectedGroup 
        ? selectedGroup.member_count || selectedGroup.members?.length || 1
        : selectedFriends.length > 0 
          ? selectedFriends.length + 1 // +1 for current user
          : 1; // Solo plan
      
      const apiData = {
        preferences: {
          groupSize: groupSize,
          budget: preferences.budget || 'medium',
          timeOfDay: planTime,
          vibe: selectedVibes[0] || 'casual',
          location: preferences.location || 'rutgers-new-brunswick',
          dietaryRestrictions: preferences.dietaryRestrictions || [],
          activities: preferences.activities || [],
          date: planDate,
          numActivities: numActivities,
          vibes: selectedVibes // Pass all selected vibes
        }
      };

      const response = await axios.post('/api/plans/curate-activities', apiData);

      if (response.data.success) {
        const activities = response.data.activities;
        setCuratedActivities(activities);
        setPrimaryActivities(activities.primary || []);
        setBackupActivities(activities.backup || []);
        
        // Create voting session
        await createVotingSession(activities, groupSize);
        
        setCurrentStep(3); // Move to activity cards step
        toast.success(`Curated ${activities.total} activities!`);
      } else {
        throw new Error('Failed to curate activities');
      }
    } catch (error) {
      console.error('Activity curation error:', error);
      toast.error('Failed to curate activities. Please try again.');
      setCurrentStep(2); // Go back to vibe selection if curation fails
    } finally {
      setIsGenerating(false);
      toast.dismiss();
    }
  };

  const refreshActivities = async () => {
    await curateActivities();
  };

  // Create voting session from curated activities
  const createVotingSession = async (activities, groupSize) => {
    try {
      if (!userProfile?.user_id) {
        console.error('User ID not available');
        return;
      }

      const response = await axios.post('/api/plans/voting-session', {
        userId: userProfile.user_id,
        preferences: {
          location: preferences.location || 'rutgers-new-brunswick',
          budget: preferences.budget || 'medium',
          vibe: selectedVibes[0] || 'casual',
          timeOfDay: planTime,
          groupSize: groupSize,
          date: planDate
        },
        activities: {
          primary: activities.primary || [],
          backup: activities.backup || []
        }
      });

      if (response.data.success) {
        setVotingSessionId(response.data.sessionId);
        console.log('✅ Voting session created:', response.data.sessionId);
      }
    } catch (error) {
      console.error('Failed to create voting session:', error);
      // Don't block the flow if voting session creation fails
    }
  };

  // Submit vote for an activity
  const submitVote = async (activityId, vote) => {
    if (!votingSessionId) {
      toast.error('Voting session not initialized');
      return;
    }

    if (!userProfile?.user_id) {
      toast.error('User not authenticated');
      return;
    }

    setVotingInProgress(prev => ({ ...prev, [activityId]: true }));

    try {
      const response = await axios.post(`/api/plans/voting-session/${votingSessionId}/vote`, {
        userId: userProfile.user_id,
        activityId: activityId,
        vote: vote
      });

      if (response.data.success) {
        // Update local state
        setActivityVotes(prev => ({
          ...prev,
          [activityId]: vote
        }));
        
        toast.success(`Voted ${vote === 'yes' ? '👍' : '👎'} on ${response.data.activity.name}`);
      }
    } catch (error) {
      console.error('Vote submission error:', error);
      toast.error('Failed to submit vote. Please try again.');
    } finally {
      setVotingInProgress(prev => ({ ...prev, [activityId]: false }));
    }
  };

  // Step 1: Group/Friends, Date, Time, Number of Activities
  const renderStep1 = () => {
  return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
      >
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Who's Going?</h2>
          <p className="text-gray-600">Select a group or invite friends (optional), then set the date and time</p>
          <p className="text-sm text-gray-500 mt-1">💡 You can create a plan for yourself too!</p>
        </div>

        {/* Groups Section */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Select a Group (Optional)
              </label>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" />
              <p className="text-sm text-gray-500 mt-2">Loading groups...</p>
            </div>
          ) : groups.length > 0 ? (
            <div>
              <div className="text-sm text-gray-500 mb-3">
                Found {groups.length} group{groups.length !== 1 ? 's' : ''}. Click to select.
            </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groups.map((group) => (
                    <button
                    key={group.group_id}
                    type="button"
                    onClick={() => {
                      console.log('Group selected:', group);
                      handleGroupSelect(group);
                    }}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left cursor-pointer ${
                      selectedGroup?.group_id === group.group_id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{group.name}</h3>
                        <p className="text-sm text-gray-600">{group.member_count || group.members?.length || 0} members</p>
                      </div>
                      {selectedGroup?.group_id === group.group_id && (
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                    </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4">No groups available. Select friends below or create a new group.</p>
          )}
            </div>

        {/* Friends Section */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Select Friends (Optional)
          </label>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" />
              <p className="text-sm text-gray-500 mt-2">Loading friends...</p>
            </div>
          ) : friends.length > 0 ? (
            <div>
              <div className="text-sm text-gray-500 mb-3">
                Found {friends.length} friend{friends.length !== 1 ? 's' : ''}. Click to select.
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {friends.map((friend) => (
                  <button
                    key={friend.user_id}
                    type="button"
                    onClick={() => {
                      console.log('Friend toggled:', friend);
                      handleFriendToggle(friend.user_id);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                      selectedFriends.includes(friend.user_id)
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {friend.name || friend.display_name || friend.email || 'Friend'}
                      </span>
                      {selectedFriends.includes(friend.user_id) ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4">No friends available. You can create a plan for yourself!</p>
          )}
            </div>

        {/* Date Selection */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Select Date
              </label>
          <input
            type="date"
            value={planDate}
            onChange={(e) => setPlanDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
          />
          </div>

        {/* Time Selection */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Time of Day
            </label>
          <div className="grid grid-cols-3 gap-3">
            {timeOptions.map((option) => (
                <button
                key={option.value}
                onClick={() => setPlanTime(option.value)}
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  planTime === option.value
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                <div className="text-2xl mb-2">{option.icon}</div>
                <div className="text-sm font-medium text-gray-900">{option.label}</div>
                </button>
              ))}
            </div>
          </div>

        {/* Number of Activities */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Number of Activities
            </label>
          <div className="flex items-center space-x-4">
                <button
              onClick={() => setNumActivities(Math.max(1, numActivities - 1))}
              className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-blue-500 flex items-center justify-center transition-colors"
            >
              <span className="text-xl">−</span>
                </button>
            <div className="flex-1 text-center">
              <span className="text-3xl font-bold text-gray-900">{numActivities}</span>
              <p className="text-sm text-gray-500">activities</p>
            </div>
            <button
              onClick={() => setNumActivities(Math.min(10, numActivities + 1))}
              className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-blue-500 flex items-center justify-center transition-colors"
            >
              <span className="text-xl">+</span>
            </button>
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Location
          </label>
          <select
            value={preferences.location}
            onChange={(e) => setPreferences(prev => ({ ...prev, location: e.target.value }))}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
          >
            {locationOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.icon} {option.label}
              </option>
            ))}
          </select>
        </div>
      </motion.div>
    );
  };

  // Step 2: Vibe Selection
  const renderStep2 = () => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
      >
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">What's the Vibe?</h2>
          <p className="text-gray-600">Select one or more vibes that match your mood</p>
          </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {vibeOptions.map((vibe) => (
            <button
              key={vibe.value}
              onClick={() => handleVibeToggle(vibe.value)}
              className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                selectedVibes.includes(vibe.value)
                  ? 'border-blue-500 bg-blue-50 shadow-md scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="text-4xl mb-3">{vibe.icon}</div>
              <h3 className="font-semibold text-gray-900 mb-1">{vibe.label}</h3>
              <p className="text-xs text-gray-600">{vibe.description}</p>
              {selectedVibes.includes(vibe.value) && (
                <div className="mt-3 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
            </button>
          ))}
        </div>

        {isGenerating && (
          <div className="mt-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" />
            <p className="text-gray-600">Generating your perfect plan...</p>
          </div>
        )}
      </motion.div>
    );
  };

  // Step 3: Curated Activities (Cards)
  const renderStep3 = () => {
    if (isGenerating) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center"
        >
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Curating Activities...</h2>
          <p className="text-gray-600">Finding the perfect activities for you</p>
        </motion.div>
      );
    }

    if (!curatedActivities || (primaryActivities.length === 0 && backupActivities.length === 0)) {
      return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center"
        >
          <p className="text-gray-600 mb-4">No activities curated yet.</p>
          <button
            onClick={handlePrevious}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            Go Back
          </button>
        </motion.div>
      );
    }

    const allActivities = [...primaryActivities, ...backupActivities];
    const currentActivity = allActivities[currentCardIndex];
    const hasMoreCards = currentCardIndex < allActivities.length - 1;
    const hasPreviousCards = currentCardIndex > 0;

    const handleVote = async (vote) => {
      if (!currentActivity) return;
      
      // Allow changing votes - submit the new vote
      await submitVote(currentActivity.id, vote);
      
      // Check if this was the last activity
      if (!hasMoreCards) {
        // Last activity voted - process votes and get top 5
        setIsProcessingVotes(true);
        await processVotesAndGetTopActivities();
      } else {
        // Move to next card after voting (with a slight delay for visual feedback)
        setTimeout(() => {
          setCurrentCardIndex(prev => prev + 1);
        }, 500);
      }
    };

    const processVotesAndGetTopActivities = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:5001/api';
        const token = await user.getIdToken();
        const currentUserId = userProfile?.user_id;
        
        // Get voting session to process votes
        const response = await axios.get(`${apiBase}/plans/voting-session/${votingSessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: {
            userId: currentUserId // Pass userId to get user's votes
          }
        });

        console.log('Voting session response:', response.data);

        if (response.data.success) {
          // The API returns response.data.session (not response.data.data)
          const session = response.data.session || response.data.data || response.data;
          
          if (!session || !session.activities) {
            console.error('Invalid session data:', session);
            console.error('Response structure:', response.data);
            toast.error('Invalid voting session data');
            setIsProcessingVotes(false);
            return;
          }
          
          // Get activities with yes votes, sorted by vote count
          // The API returns activities with userVote field (not userVotes array)
          const activitiesWithVotes = session.activities
            .filter(activity => {
              // Check if current user voted yes (API returns userVote field)
              return activity.userVote === 'yes';
            })
            .sort((a, b) => {
              // Sort by yes votes (for solo, this will be 1 or 0)
              return (b.votes?.yes || 0) - (a.votes?.yes || 0);
            })
            .slice(0, 5); // Top 5

          console.log('Activities with yes votes:', activitiesWithVotes);

          if (activitiesWithVotes.length > 0) {
            // setTopActivities(activitiesWithVotes); // Reserved for future use
            setRankingActivities(activitiesWithVotes); // Initialize ranking order
            setCurrentStep(4); // Move to ranking step
          } else {
            toast.error('No activities received votes. Please vote on some activities.');
            setIsProcessingVotes(false);
          }
        } else {
          console.error('Failed to get voting session:', response.data);
          toast.error('Failed to retrieve voting session');
          setIsProcessingVotes(false);
        }
      } catch (error) {
        console.error('Error processing votes:', error);
        console.error('Error details:', error.response?.data);
        toast.error('Failed to process votes');
        setIsProcessingVotes(false);
      }
    };


    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Curated Activities</h2>
              <p className="text-gray-600 mt-1">
                {currentCardIndex + 1} of {allActivities.length} • {primaryActivities.length} primary + {backupActivities.length} backups
              </p>
            </div>
            <button
              onClick={refreshActivities}
              disabled={isGenerating}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <RefreshCw size={16} className={isGenerating ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
          {/* Progress Bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentCardIndex + 1) / allActivities.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Stacked Card Container */}
        <div className="relative flex items-center justify-center min-h-[600px] px-24">
          {currentActivity ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentActivity.id || currentCardIndex}
                initial={{ opacity: 0, scale: 0.9, x: 0 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.9, x: 0 }}
                transition={{ duration: 0.3 }}
                className="relative w-full max-w-md mx-auto"
              >
                {/* Card */}
                <div className={`bg-white rounded-3xl shadow-2xl border-2 transition-all duration-200 ${
                  currentActivity.isBackup 
                    ? 'border-gray-300' 
                    : 'border-blue-200'
                }`}>
                  <div className="p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentActivity.name}</h3>
                        <div className="flex items-center space-x-2 flex-wrap gap-2">
                          {/* Show detailed category if available, otherwise show type */}
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                            {currentActivity.detailedCategory || currentActivity.type}
                          </span>
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                            currentActivity.price === 'Free' 
                              ? 'bg-green-100 text-green-700'
                              : currentActivity.price === '$'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {currentActivity.price}
                          </span>
                          {currentActivity.isBackup && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                              Backup
                            </span>
                          )}
                        </div>
                      </div>
                      {currentActivity.rating > 0 && (
                        <div className="flex items-center space-x-1">
                          <span className="text-yellow-500 text-xl">⭐</span>
                          <span className="text-lg font-semibold text-gray-700">{currentActivity.rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-6 line-clamp-4">
                      {currentActivity.description}
                    </p>

                    {/* Location with Neighborhood (if enriched) */}
                    {(currentActivity.location || currentActivity.neighborhood) && (
                      <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                        <MapPin className="w-5 h-5" />
                        <span className="truncate">
                          {currentActivity.neighborhood && (
                            <span className="font-medium text-gray-700">{currentActivity.neighborhood}</span>
                          )}
                          {currentActivity.neighborhood && currentActivity.location && ' • '}
                          {currentActivity.location && (
                            <span>{currentActivity.location}</span>
                          )}
                        </span>
                      </div>
                    )}

                    {/* Hours Status (enriched) or Hours (fallback) */}
                    {(currentActivity.hoursStatus || currentActivity.hours) && (
                      <div className="flex items-center space-x-2 text-sm mb-4">
                        <Clock className="w-5 h-5 text-gray-500" />
                        <span className={`font-medium ${
                          currentActivity.hoursStatus === 'Open Now' 
                            ? 'text-green-600'
                            : currentActivity.hoursStatus === 'Closed'
                            ? 'text-red-600'
                            : 'text-gray-600'
                        }`}>
                          {currentActivity.hoursStatus || currentActivity.hours}
                        </span>
                      </div>
                    )}

                    {/* Tags */}
                    {currentActivity.tags && currentActivity.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {currentActivity.tags.slice(0, 4).map((tag, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Vibe Badge */}
                    <div className="mb-4 pt-4 border-t border-gray-200">
                      <span className="text-sm text-gray-500">Vibe: </span>
                      <span className="text-base font-medium text-gray-700 capitalize">{currentActivity.vibe}</span>
                    </div>

                    {/* Contact Information */}
                    {(currentActivity.website || currentActivity.tel || currentActivity.socialMedia) && (
                      <div className="mb-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-3 text-sm">
                          {currentActivity.website && (
                            <a
                              href={currentActivity.website.startsWith('http') ? currentActivity.website : `https://${currentActivity.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              <span>🌐</span>
                              <span className="truncate max-w-[200px]">Website</span>
                            </a>
                          )}
                          {currentActivity.tel && (
                            <a
                              href={`tel:${currentActivity.tel.replace(/\s/g, '')}`}
                              className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              <span>📞</span>
                              <span>{currentActivity.tel}</span>
                            </a>
                          )}
                          {currentActivity.socialMedia && (
                            <div className="flex items-center space-x-2">
                              {currentActivity.socialMedia.facebook && (
                                <a
                                  href={currentActivity.socialMedia.facebook}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Facebook"
                                >
                                  📘
                                </a>
                              )}
                              {currentActivity.socialMedia.twitter && (
                                <a
                                  href={currentActivity.socialMedia.twitter}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-500"
                                  title="Twitter"
                                >
                                  🐦
                                </a>
                              )}
                              {currentActivity.socialMedia.instagram && (
                                <a
                                  href={currentActivity.socialMedia.instagram}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-pink-600 hover:text-pink-700"
                                  title="Instagram"
                                >
                                  📷
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Vote Status */}
                    {activityVotes[currentActivity.id] && (
                      <div className={`mb-4 p-3 rounded-lg text-center ${
                        activityVotes[currentActivity.id] === 'yes'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700'
                      }`}>
                        <p className="font-medium">
                          You voted {activityVotes[currentActivity.id] === 'yes' ? '👍 Yes' : '👎 No'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Back Button - Top Center */}
                {hasPreviousCards && (
                  <button
                    onClick={() => setCurrentCardIndex(currentCardIndex - 1)}
                    className="absolute -top-16 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white hover:bg-gray-50 border-2 border-gray-300 shadow-lg flex items-center justify-center text-2xl transition-all duration-200 hover:scale-110 z-10"
                    title="Go back to previous card"
                  >
                    ←
                  </button>
                )}
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No more activities to review!</p>
            </div>
          )}

          {/* Side Action Buttons - Outside card container */}
          {currentActivity && (
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex items-center justify-between pointer-events-none px-8">
              {/* Left Button - No */}
              <button
                onClick={() => handleVote('no')}
                disabled={votingInProgress[currentActivity.id]}
                className={`pointer-events-auto w-20 h-20 rounded-full shadow-xl flex items-center justify-center text-4xl transition-all duration-200 ${
                  activityVotes[currentActivity.id] === 'no'
                    ? 'bg-red-500 scale-110 shadow-red-300'
                    : activityVotes[currentActivity.id] === 'yes'
                    ? 'bg-white border-4 border-red-300 opacity-60 hover:opacity-100'
                    : 'bg-white hover:bg-red-50 border-4 border-red-300 hover:scale-110 hover:shadow-lg'
                } ${votingInProgress[currentActivity.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                👎
              </button>

              {/* Right Button - Yes */}
              <button
                onClick={() => handleVote('yes')}
                disabled={votingInProgress[currentActivity.id]}
                className={`pointer-events-auto w-20 h-20 rounded-full shadow-xl flex items-center justify-center text-4xl transition-all duration-200 ${
                  activityVotes[currentActivity.id] === 'yes'
                    ? 'bg-green-500 scale-110 shadow-green-300'
                    : activityVotes[currentActivity.id] === 'no'
                    ? 'bg-white border-4 border-green-300 opacity-60 hover:opacity-100'
                    : 'bg-white hover:bg-green-50 border-4 border-green-300 hover:scale-110 hover:shadow-lg'
                } ${votingInProgress[currentActivity.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                👍
              </button>
            </div>
          )}

          {/* Processing indicator when last activity is voted */}
          {isProcessingVotes && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
              <div className="flex items-center space-x-3 px-6 py-3 bg-blue-500 text-white rounded-full font-semibold shadow-lg">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing votes...</span>
              </div>
            </div>
          )}
        </div>

        {/* Info Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Next Step:</strong> These activities will be sent to your group members for voting. 
            Each member can swipe left (pass) or right (like) on activities. 
            The most popular activities will be used to generate your final plan!
          </p>
        </div>
      </motion.div>
    );
  };

  // Sortable Item Component for drag and drop
  const SortableItem = ({ id, activity, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`bg-white rounded-xl shadow-md border-2 border-gray-200 p-4 mb-3 flex items-center space-x-4 ${
          isDragging ? 'border-blue-500' : ''
        }`}
      >
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
            <span className="text-lg">⋮⋮</span>
          </div>
        </div>
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg">
          {index + 1}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{activity.name}</h3>
          <p className="text-sm text-gray-600">{activity.detailedCategory || activity.type}</p>
          {activity.neighborhood && (
            <p className="text-xs text-gray-500 mt-1">{activity.neighborhood}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
            activity.price === 'Free' 
              ? 'bg-green-100 text-green-700'
              : activity.price === '$'
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-purple-100 text-purple-700'
          }`}>
            {activity.price}
          </span>
        </div>
      </div>
    );
  };

  // Step 4: Ranking Activities
  const renderStep4 = () => {
    const handleDragEnd = (event) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        setRankingActivities((items) => {
          const oldIndex = items.findIndex(item => item.id === active.id);
          const newIndex = items.findIndex(item => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    };

    const handleFinalizeRanking = async () => {
      if (rankingActivities.length === 0) {
        toast.error('No activities to generate itinerary from');
        return;
      }

      try {
        setIsGeneratingItinerary(true);
        const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:5001/api';
        const token = await user.getIdToken();
        
        // Get numActivities from step 1 (stored in state)
        const activitiesToInclude = numActivities || rankingActivities.length;
        
        console.log('🎯 Generating itinerary from ranked activities:', {
          sessionId: votingSessionId,
          numRanked: rankingActivities.length,
          numToInclude: activitiesToInclude,
          preferences: preferences
        });

        const response = await axios.post(`${apiBase}/plans/generate-itinerary`, {
          sessionId: votingSessionId,
          rankedActivities: rankingActivities,
          preferences: {
            ...preferences,
            date: planDate,
            numActivities: activitiesToInclude
          },
          userId: userProfile?.user_id
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          setGeneratedPlan(response.data.plan);
          toast.success('Itinerary generated successfully!');
          console.log('✅ Generated plan:', response.data.plan);
          // TODO: Navigate to plan view or show plan in a new step
        } else {
          throw new Error(response.data.error || 'Failed to generate itinerary');
        }
      } catch (error) {
        console.error('Error generating itinerary:', error);
        toast.error(error.response?.data?.error || 'Failed to generate itinerary');
      } finally {
        setIsGeneratingItinerary(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Rank Your Top Activities</h2>
          <p className="text-gray-600">
            Drag and drop to reorder your top 5 activities. Your most preferred activity should be at the top.
          </p>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rankingActivities.map(a => a.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              {rankingActivities.length > 0 ? (
                rankingActivities.map((activity, index) => (
                  <SortableItem
                    key={activity.id}
                    id={activity.id}
                    activity={activity}
                    index={index}
                  />
                ))
              ) : (
                <p className="text-gray-600 text-center py-8">No activities to rank</p>
              )}
            </div>
          </SortableContext>
        </DndContext>

        {isGeneratingItinerary ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-500 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Generating Your Itinerary...</h3>
            <p className="text-gray-600">
              Creating a detailed plan with transportation and weather considerations
            </p>
          </div>
        ) : generatedPlan ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Your Plan is Ready! 🎉</h3>
              <button
                onClick={() => setGeneratedPlan(null)}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
              >
                Edit Ranking
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">{generatedPlan.title}</h4>
                <p className="text-gray-600">{generatedPlan.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Estimated Cost: </span>
                  <span className="text-gray-600">{generatedPlan.estimatedCost}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Duration: </span>
                  <span className="text-gray-600">{generatedPlan.duration}</span>
                </div>
              </div>
              {generatedPlan.itinerary && generatedPlan.itinerary.length > 0 && (
                <div className="mt-6">
                  <h5 className="font-semibold text-gray-800 mb-3 text-lg">Itinerary:</h5>
                  <div className="space-y-3">
                    {generatedPlan.itinerary.map((item, idx) => (
                      <div key={idx} className="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-r-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-900">{item.time}</span>
                          {item.travelTime && item.travelTime !== 'N/A' && item.travelTime.trim() !== '' && (
                            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">→ {item.travelTime}</span>
                          )}
                        </div>
                        <p className="text-gray-700 font-medium">{item.venue}</p>
                        <p className="text-sm text-gray-600 mt-1">{item.activity}</p>
                        {item.location && (
                          <p className="text-xs text-gray-500 mt-1">📍 {item.location}</p>
                        )}
                        {item.transportation && (
                          <p className="text-xs text-blue-600 mt-2 font-medium">🚗 {item.transportation}</p>
                        )}
                        {item.cost && (
                          <p className="text-xs text-gray-500 mt-1">💰 {item.cost}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {generatedPlan.tips && generatedPlan.tips.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="font-semibold text-gray-800 mb-2">Tips:</h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {generatedPlan.tips.map((tip, idx) => (
                      <li key={idx}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
              {generatedPlan.transportationSummary && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="font-semibold text-gray-800 mb-2">Transportation:</h5>
                  <p className="text-sm text-gray-600">{generatedPlan.transportationSummary}</p>
                </div>
              )}
              {generatedPlan.weatherConsideration && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="font-semibold text-gray-800 mb-2">Weather:</h5>
                  <p className="text-sm text-gray-600">{generatedPlan.weatherConsideration}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevious}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-all"
            >
              ← Back
            </button>
            <button
              onClick={handleFinalizeRanking}
              disabled={isGeneratingItinerary || rankingActivities.length === 0}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingItinerary ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Generating...
                </>
              ) : (
                'Finalize Plan →'
              )}
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return renderStep1();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Generate Your Plan ✨
          </h1>
          <p className="text-gray-600 text-lg">
            {currentStep === 1 && "Let's set up your perfect night out"}
            {currentStep === 2 && "Choose the vibe that matches your mood"}
            {currentStep === 3 && "Here are your curated activities!"}
            {currentStep === 4 && "Rank your top activities"}
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-700">Step {currentStep} of {totalSteps}</span>
              <span className="text-sm font-semibold text-blue-600">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>
        </motion.div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        {currentStep < 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-center"
          >
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                currentStep === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span>Previous</span>
            </button>

            <button
              onClick={handleNext}
              disabled={isGenerating || (currentStep === 2 && selectedVibes.length === 0)}
              className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${
                isGenerating || (currentStep === 2 && selectedVibes.length === 0)
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-gradient-to-r from-blue-500 to-green-500 hover:shadow-lg text-white'
              }`}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : currentStep === 2 ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Generate Plan</span>
                </>
              ) : (
                <>
                  <span>Next</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlanGenerator; 
